/**
 * Откуда пришли внутри CRM — для кнопки «Назад» на странице интервью.
 *
 * Зачем. Сервер отдаёт заголовок `Referrer-Policy: no-referrer` (helmet в
 * server/_core/index.ts), поэтому `document.referrer` у нас всегда пуст, а в
 * одностраничном приложении он и без того помнит только первую загрузку.
 * Проверка 28.09.2026 показала: «Назад» по источнику перехода не срабатывал ни
 * разу. Поэтому переходы внутри вкладки помним сами.
 *
 * Как. wouter при каждом переходе вызывает history.pushState/replaceState и
 * объявляет об этом событием окна с тем же именем; кнопки браузера дают
 * `popstate`. Память живёт до перезагрузки страницы: после неё она пуста, и
 * «Назад» ведёт по умолчанию — в список встреч.
 *
 * Правила перехода — чистые функции без окна, их проверяют тесты.
 */

export type NavigationKind = "push" | "replace" | "pop";

export interface NavigationMemory {
  /** Адрес, на котором вкладка сейчас (путь + параметры). */
  current: string;
  /** Предыдущая страница этой вкладки; undefined — неизвестна. */
  previous: string | undefined;
}

/**
 * Следующее состояние памяти.
 * • push — ушли на новую страницу: текущая становится предыдущей;
 * • replace — та же страница сменила адрес (фильтры, вкладка): предыдущая
 *   прежняя;
 * • pop — кнопки браузера: куда именно шагнули по истории, неизвестно,
 *   поэтому предыдущую забываем, а не угадываем.
 */
export function nextNavigationMemory(
  memory: NavigationMemory,
  kind: NavigationKind,
  path: string
): NavigationMemory {
  if (kind === "replace") return { current: path, previous: memory.previous };
  if (kind === "pop") return { current: path, previous: undefined };
  if (path === memory.current) return memory;
  return { current: path, previous: memory.current };
}

/**
 * Откуда пришли на страницу `here`. Если память ещё не успела узнать о
 * переходе (текущая запись — не `here`), значит пришли именно с неё.
 */
export function cameFromIn(
  memory: NavigationMemory | undefined,
  here: string
): string | undefined {
  if (!memory) return undefined;
  if (memory.current === here) return memory.previous;
  return memory.current;
}

let memory: NavigationMemory | undefined;

const pathOf = (target: Window) =>
  `${target.location.pathname}${target.location.search}`;

/**
 * Начать помнить переходы. Вызывается один раз при запуске приложения
 * (main.tsx), до первого перехода; повторный вызов ничего не делает.
 */
export function rememberInAppNavigation(target: Window): void {
  if (memory) return;
  memory = { current: pathOf(target), previous: undefined };
  const events: ReadonlyArray<[string, NavigationKind]> = [
    ["pushState", "push"],
    ["replaceState", "replace"],
    ["popstate", "pop"],
  ];
  for (const [event, kind] of events) {
    target.addEventListener(event, () => {
      if (memory) memory = nextNavigationMemory(memory, kind, pathOf(target));
    });
  }
}

/** С какой страницы этой вкладки пришли на `here`; undefined — неизвестно. */
export function inAppCameFrom(here: string): string | undefined {
  return cameFromIn(memory, here);
}
